import React, { useEffect, useState } from "react";
import axios from "axios";
import { Base_URL } from "../url";
import { useAuth } from "../Context/AuthContext";
import "../Styling/ViewResourceDetails.css";
import { FiUsers, FiSearch } from "react-icons/fi";
import { FaBuilding } from "react-icons/fa";
import { MdBadge } from "react-icons/md";
import { HiOutlineOfficeBuilding } from "react-icons/hi";
import { User, IdCard, Building2 } from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const ViewResourceDetails = () => {
  const { user } = useAuth();

  const [resources, setResources] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchResources = async () => {
    try {
      const res = await axios.get(
        `${Base_URL}/ResourceMaster/all`,
        {
          params: {
            orgId: user?.organisationId,
          },
        },
      );

      const data = res?.data?.data || res?.data?.Data || res?.data || [];
      const arrayData = Array.isArray(data) ? data : [data];
      const orgFilteredData = arrayData.filter(
        (item) => item.organisationId === user?.organisationId
      );
      setResources(orgFilteredData);
    } catch (err) {
      console.error("Error fetching resources:", err);
    }
  };

  useEffect(() => {
    if (user?.organisationId) {
      fetchResources();
    }
  }, [user]);

  const filteredResources = resources.filter((item) =>
    item.resourceName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const downloadExcel = () => {
    if (!filteredResources.length) return;

    const exportData = filteredResources.map((item) => ({
      Name: item.resourceName,
      ResourceID: item.resourceId,
      DOB: item.dob,
      JoiningDate: item.dateOfJoining,
      Role: item.offerRoleDesc,
      Company: item.name,
      Office: item.officeName,
      Organisation: item.organisationName,
      Username: item.username,
      Basic: item.basic,
      HRA: item.hra,
      Medical: item.medicalAllowance,
      Conveyance: item.conveyanceAllowance,
      Special: item.specialAllowance,
      Bonus: item.advanceStatutoryBonus,
      ChildEdu: item.childEducationAllowance,
      Gross: item.gross,
      PF: item.pfEmployee,
      BankName: item.bankName,
      AccountNo: item.accountNo,
      IFSC: item.ifscCode,
      Email: item.emailId,
      Phone: item.phoneNumber,
      PAN: item.panNumber,
      Aadhar: item.aadharNumber,
      Active: item.active ? "Active" : "Inactive",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const columnWidths = Object.keys(exportData[0]).map((key) => {
  const maxLength = Math.max(
    key.length,
    ...exportData.map((row) =>
      row[key] ? row[key].toString().length : 0
    )
  );

  return { wch: maxLength + 4 };
});

worksheet["!cols"] = columnWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Resources");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    saveAs(data, "ResourceDetails.xlsx");
  };

  return (
    <div className="resource-dashboard-container">

      {/* Header */}
      <div className="resource-dashboard-header">
        <h2>Resource Details</h2>
        <button className="excel-btn" onClick={downloadExcel}>
          Download Excel
        </button>
      </div>

      {/* Summary Label */}
      <div style={{
        padding: "14px 24px 0 24px",
        fontSize: "10px",
        fontWeight: 700,
        color: "#9ca3af",
        textTransform: "uppercase",
        letterSpacing: "0.07em",
        background: "#ffffff",
      }}>
        Resource Summary
      </div>

      {/* Summary Cards */}
      <div className="resource-dashboard-summary">

        <div className="resource-card highlight">
          <div className="card-row">
            <FiUsers className="card-icon blue" />
            <div className="card-text">
              <p>Total Resources</p>
              <h3>{resources.length}</h3>
            </div>
          </div>
        </div>

        <div className="resource-card accent">
          <div className="card-row">
            <User className="card-icon orange" />
            <div className="card-text">
              <p>Active Resources</p>
              <h3>{resources.filter(r => r.active).length}</h3>
            </div>
          </div>
        </div>

        <div className="resource-card">
          <div className="card-row">
            <IdCard className="card-icon purple" />
            <div className="card-text">
              <p>Inactive Resources</p>
              <h3>{resources.filter(r => !r.active).length}</h3>
            </div>
          </div>
        </div>

        <div className="resource-card">
          <div className="card-row">
            <Building2 className="card-icon gray" />
            <div className="card-text">
              <p>Organisation ID</p>
              <h3>{user?.organisationId || "N/A"}</h3>
            </div>
          </div>
        </div>

      </div>

      {/* Search Bar */}
      <div className="resource-search-bar">
        <div className="search-input-wrapper">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {searchTerm && (
          <button onClick={() => setSearchTerm("")}>Clear</button>
        )}
      </div>

      {/* Table */}
      <div className="resource-dashboard-table-wrapper">
        <table className="resource-dashboard-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Resource ID</th>
              <th>DOB</th>
              <th>Joining Date</th>
              <th>Role</th>
              <th>Active</th>
              <th>Company Name</th>
              <th>Office</th>
              <th>Organisation</th>
              <th>Username</th>
              <th>Basic</th>
              <th>HRA</th>
              <th>Medical</th>
              <th>Conveyance</th>
              <th>Special</th>
              <th>Bonus</th>
              <th>Child Edu</th>
              <th>Gross</th>
              <th>PF</th>
              <th>Bank Name</th>
              <th>Account No</th>
              <th>IFSC Code</th>
              <th>Email</th>
              <th>Payment Bank</th>
              <th>Cost Center</th>
              <th>Default Location</th>
              <th>Office Code</th>
              <th>Father's Name</th>
              <th>Spouse Name</th>
              <th>Marital Status</th>
              <th>Phone</th>
              <th>PAN</th>
              <th>Aadhar</th>
              <th>Blood Group</th>
              <th>UAN</th>
              <th>Emergency Name</th>
              <th>Emergency Relation</th>
              <th>Emergency Phone</th>
              <th>Mav User ID</th>

              <th>Leave Policy</th>
              <th>Status Reason</th>
              <th>Line Manager</th>
              <th>Dot Line Manager</th>
              <th>Left Date</th>
              <th>Gender</th>
              <th>Confirmation Date</th>
            </tr>
          </thead>

          <tbody>
            {filteredResources.length > 0 ? (
              filteredResources.map((item, index) => (
                <tr key={index}>

                  <td className="name-cell">
                    <div className="avatar">
                      {item.resourceName?.charAt(0)}
                    </div>
                    <div className="name-content">
                      <span>{item.resourceName}</span>
                      <small>{item.offerRoleDesc}</small>
                    </div>
                  </td>

                  <td>
                    <span className="resource-id-chip">
                      {item.resourceId}
                    </span>
                  </td>

                  <td>
                    {item.dob ? new Date(item.dob).toLocaleDateString() : ""}
                  </td>

                  <td>
                    {item.dateOfJoining
                      ? new Date(item.dateOfJoining).toLocaleDateString()
                      : ""}
                  </td>

                  <td>{item.offerRoleDesc}</td>
                  <td>
                    <span
                      className={`status-badge ${item.active ? "status-paid" : "status-draft"
                        }`}
                    >
                      {item.active ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </td>

                  <td>
                    <span className="cell-with-icon">
                      <FaBuilding className="table-icon colored" />
                      {item.name}
                    </span>
                  </td>

                  <td>
                    <span className="cell-with-icon">
                      <HiOutlineOfficeBuilding className="table-icon purple" />
                      {item.officeName}
                    </span>
                  </td>

                  <td>
                    <span className="cell-with-icon">
                      <MdBadge className="table-icon green" />
                      {item.organisationName}
                    </span>
                  </td>

                  <td>{item.username}</td>

                  <td>₹{item.basic}</td>
                  <td>₹{item.hra}</td>
                  <td>₹{item.medicalAllowance}</td>
                  <td>₹{item.conveyanceAllowance}</td>
                  <td>₹{item.specialAllowance}</td>
                  <td>₹{item.advanceStatutoryBonus}</td>
                  <td>₹{item.childEducationAllowance}</td>
                  <td>₹{item.gross}</td>
                  <td>₹{item.pfEmployee}</td>

                  <td>{item.bankName || "-"}</td>
                  <td>{item.accountNo || "-"}</td>
                  <td>{item.ifscCode || "-"}</td>
                  <td>{item.emailId || "-"}</td>
                  <td>{item.paymentBank || "-"}</td>
                  <td>{item.costCenter || "-"}</td>
                  <td>{item.defaultLocation || "-"}</td>
                  <td>{item.officeCode || "-"}</td>
                  <td>{item.fathersName || "-"}</td>
                  <td>{item.spouseName || "-"}</td>
                  <td>{item.maritalStatus || "-"}</td>
                  <td>{item.phoneNumber || "-"}</td>
                  <td>{item.panNumber || "-"}</td>
                  <td>{item.aadharNumber || "-"}</td>
                  <td>{item.bloodGroup || "-"}</td>
                  <td>{item.uanNumber || "-"}</td>
                  <td>{item.emergencyContactName || "-"}</td>
                  <td>{item.emergencyContactRelation || "-"}</td>
                  <td>{item.emergencyContactPhoneNumber || "-"}</td>
                  <td>{item.mavUserID || "-"}</td>



                  <td>{item.leavePolicyId || "-"}</td>
                  <td>{item.statusReason || "-"}</td>
                  <td>{item.lineManagerId || "-"}</td>
                  <td>{item.dotLineManagerId || "-"}</td>

                  <td>
                    {item.leftDate
                      ? new Date(item.leftDate).toLocaleDateString()
                      : "-"}
                  </td>

                  <td>{item.gender || "-"}</td>

                  <td>
                    {item.confirmationDate
                      ? new Date(item.confirmationDate).toLocaleDateString()
                      : "-"}
                  </td>

                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="50" className="no-data">
                  No Data Found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ViewResourceDetails;